import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Badge,
  Button,
} from "@mui/material";
import {
  Edit as EditIcon,
  Settings as ManageIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Casino as CasinoIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Schedule as DelayedIcon,
  Info as InterestIcon,
} from "@mui/icons-material";

import PrimaryTableCell from "app/Components/Tables/PrimaryTableCell";
import StyledTableRow from "app/Components/Tables/StyledTableRow";
import AllRequests from "./AllRequests.jsx";

function GameManager() {
  const hostGames = useSelector((state) => state.host.gamelist);
  const requests = useSelector((state) => state.requests.receivedRequests);
  const navigate = useNavigate();

  const getRequestCount = (gameId: string) => {
    if (!requests) return 0;
    return Object.keys(requests).filter(
      (reqKey) =>
        requests[reqKey].gameId === gameId &&
        requests[reqKey].status === "pending",
    ).length;
  };

  const getStatusColor = (status: string, visible: boolean) => {
    if (!visible) return "default";
    switch (status.toLowerCase()) {
      case "active":
      case "running":
        return "success";
      case "pending":
        return "warning";
      case "completed":
        return "info";
      case "delayed":
        return "error";
      case "interest":
        return "primary";
      default:
        return "default";
    }
  };

  const getAccessIcon = (access: string) => {
    return access === "Private" ? <LockIcon /> : <PublicIcon />;
  };

  const getStatusIcon = (status: string, visible: boolean) => {
    if (!visible) return <VisibilityOffIcon />;
    
    switch (status.toLowerCase()) {
      case "delayed":
        return <DelayedIcon />;
      case "interest":
        return <InterestIcon />;
      default:
        return <VisibilityIcon />;
    }
  };

  const getDayName = (dayNumber: number) => {
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    return days[dayNumber] || "Unknown";
  };

  const getStakesDisplay = (gameData: any) => {
    if (gameData.type === "Tournament") {
      const buyin = gameData.buyin || "0";
      const fee = gameData.fee || "0";
      return buyin === "0" ? "Free" : `$${buyin} + $${fee}`;
    } else {
      // Cash game
      return gameData.stakes ? `$${gameData.stakes}` : "Stakes not set";
    }
  };

  const games = Object.keys(hostGames);

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h2"
          gutterBottom
          sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}
        >
          <ManageIcon color="primary" />
          Manage Games
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ textAlign: "center" }}>
          View and manage all your hosted poker games
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <AllRequests />
      </Box>

      {games.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: "center", py: 6 }}>
            <CasinoIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
            <Typography variant="h5" color="textSecondary" gutterBottom>
              No games created yet
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Create your first poker game to get started
            </Typography>
            <Button
              variant="contained"
              startIcon={<CasinoIcon />}
              onClick={() => navigate("/create")}
            >
              Create Game
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mb: 2 }}>
            <CasinoIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Your Games
            </Typography>
          </Box>

          <TableContainer 
            component={Paper} 
            elevation={5}
            sx={{ borderRadius: "20px" }}
          >
            <Table className="tablelist">
              <TableHead>
                <StyledTableRow>
                  <PrimaryTableCell>Game Name</PrimaryTableCell>
                  <PrimaryTableCell align="center">Type</PrimaryTableCell>
                  <PrimaryTableCell>Start Day</PrimaryTableCell>
                  <PrimaryTableCell>Stakes</PrimaryTableCell>
                  <PrimaryTableCell>Access</PrimaryTableCell>
                  <PrimaryTableCell>Status</PrimaryTableCell>
                  <PrimaryTableCell align="center">Actions</PrimaryTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {games.map((gameKey) => {
                  const gameData = hostGames[gameKey];
                  const requestCount = getRequestCount(gameData.id);

                  return (
                    <StyledTableRow
                      key={gameData.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => navigate(`/manage/${gameData.id}`)}
                    >
                      <PrimaryTableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/games/${gameData.id}`);
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          <CasinoIcon color="primary" />
                          <Typography variant="body1" fontWeight="medium">
                            {gameData.gameName}
                          </Typography>
                        </Box>
                      </PrimaryTableCell>

                      <PrimaryTableCell align="center">
                        <Chip
                          label={gameData.type}
                          size="small"
                          variant="filled"
                          color={
                            gameData.type === "Tournament"
                              ? "primary"
                              : "warning"
                          }
                          sx={{
                            fontWeight: 600,
                            color: "white",
                          }}
                        />
                      </PrimaryTableCell>

                      <PrimaryTableCell>
                        <Typography variant="body2">
                          {getDayName(gameData.startDay)}
                        </Typography>
                        {gameData.startTime && (
                          <Typography variant="caption" color="textSecondary">
                            {gameData.startTime}
                          </Typography>
                        )}
                      </PrimaryTableCell>

                      <PrimaryTableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {getStakesDisplay(gameData)}
                        </Typography>
                      </PrimaryTableCell>

                      <PrimaryTableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {getAccessIcon(gameData.access)}
                          <Typography variant="body2">
                            {gameData.access}
                          </Typography>
                        </Box>
                      </PrimaryTableCell>

                      <PrimaryTableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Chip
                            label={
                              gameData.visible === false
                                ? "Hidden"
                                : gameData.status
                            }
                            size="small"
                            color={getStatusColor(
                              gameData.status,
                              gameData.visible,
                            )}
                            icon={getStatusIcon(gameData.status, gameData.visible)}
                            sx={{
                              fontWeight: 500,
                            }}
                          />
                        </Box>
                      </PrimaryTableCell>

                      <PrimaryTableCell
                        align="center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "center",
                          }}
                        >
                          <Tooltip title="Manage Game">
                            <Badge badgeContent={requestCount} color="error">
                              <IconButton
                                color="primary"
                                onClick={() =>
                                  navigate(`/manage/${gameData.id}`)
                                }
                                size="small"
                              >
                                <ManageIcon />
                              </IconButton>
                            </Badge>
                          </Tooltip>

                          <Tooltip title="Edit Game Details">
                            <IconButton
                              color="secondary"
                              onClick={() =>
                                navigate(`/editgame/${gameData.id}`)
                              }
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </PrimaryTableCell>
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}

export default GameManager;
